import { authenticatedFetch } from '@src/utility/authenticatedFetch';
import { LogManager } from '@src/utility/Logger';

import { err, ok } from './common/Result';

import type { Result } from './common/Result';

const logger = LogManager.getLogger('service:ZoomService');

export interface ZoomRecordingFile {
  id: string;
  file_type: string;
  file_size: number;
  file_extension: string;
  recording_type: string;
  download_url: string;
  play_url: string;
  status: string;
  recording_start: string;
  recording_end: string;
}

export interface ZoomRecording {
  id: string;
  uuid: string;
  topic: string;
  start_time: string;
  duration: number;
  total_size: number;
  recording_count: number;
  share_url: string;
  password: string;
  has_video: boolean;
  has_transcript: boolean;
  recording_files: ZoomRecordingFile[];
  video_download_url: string | null;
  video_play_url: string | null;
}

export interface PaginatedRecordingsResponse {
  recordings: ZoomRecording[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface SearchRecordingsResponse {
  recordings: ZoomRecording[];
  total: number;
  query: string;
}

export interface CreateStoryFromRecordingParams {
  recordingId: string;
  recordingFiles: ZoomRecordingFile[];
  personId?: string;
  email?: string;
  password?: string;
}

export class ZoomService {
  constructor(
    private readonly apiUrl: string,
    private readonly getAuthToken: () => Promise<string | undefined>,
  ) {}

  async listRecordings(
    personId?: string,
    email?: string,
  ): Promise<Result<ZoomRecording[]>> {
    try {
      const params = new URLSearchParams();
      if (personId) params.append('personId', personId);
      if (email) params.append('email', email);

      const response = await authenticatedFetch(
        `${this.apiUrl}/api/zoom/list-recordings?${params.toString()}`,
        {},
        this.getAuthToken,
      );

      if (!response.ok) {
        return err('EXTERNAL', 'Failed to fetch recordings');
      }

      const data = await response.json();
      return ok(data.recordings || []);
    } catch (error) {
      logger.error('Failed to fetch Zoom recordings:', error);
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Unknown error',
      );
    }
  }

  async listRecordingsPaginated(
    personId?: string,
    email?: string,
    page: number = 1,
    pageSize: number = 10,
    refresh?: boolean,
  ): Promise<Result<PaginatedRecordingsResponse>> {
    try {
      const params = new URLSearchParams();
      if (personId) params.append('personId', personId);
      if (email) params.append('email', email);
      params.append('page', String(page));
      params.append('pageSize', String(pageSize));
      if (refresh) params.append('refresh', 'true');

      const response = await authenticatedFetch(
        `${this.apiUrl}/api/zoom/list-recordings?${params.toString()}`,
        {},
        this.getAuthToken,
      );

      if (!response.ok) {
        return err('EXTERNAL', 'Failed to fetch recordings');
      }

      const data = await response.json();
      return ok({
        recordings: data.recordings || [],
        total: data.total ?? 0,
        page: data.page ?? page,
        pageSize: data.pageSize ?? pageSize,
        hasMore: data.hasMore ?? false,
      });
    } catch (error) {
      logger.error('Failed to fetch paginated Zoom recordings:', error);
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Unknown error',
      );
    }
  }

  async searchRecordings(
    personId?: string,
    email?: string,
    query?: string,
  ): Promise<Result<SearchRecordingsResponse>> {
    try {
      const params = new URLSearchParams();
      if (personId) params.append('personId', personId);
      if (email) params.append('email', email);
      if (query) params.append('search', query);

      const response = await authenticatedFetch(
        `${this.apiUrl}/api/zoom/list-recordings?${params.toString()}`,
        {},
        this.getAuthToken,
      );

      if (!response.ok) {
        return err('EXTERNAL', 'Failed to search recordings');
      }

      const data = await response.json();
      return ok({
        recordings: data.recordings || [],
        total: data.total ?? 0,
        query: data.query ?? '',
      });
    } catch (error) {
      logger.error('Failed to search Zoom recordings:', error);
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Unknown error',
      );
    }
  }

  async checkAuth(
    personId?: string,
    email?: string,
  ): Promise<Result<{ authenticated: boolean; email?: string }>> {
    try {
      const params = new URLSearchParams();
      if (personId) params.append('personId', personId);
      if (email) params.append('email', email);

      const response = await fetch(
        `${this.apiUrl}/api/zoom/get-zoom-info?${params.toString()}`,
      );

      if (!response.ok) {
        return err('EXTERNAL', 'Failed to check Zoom auth');
      }

      const data = await response.json();
      return ok({ authenticated: data.authenticated, email: data.email });
    } catch (error) {
      logger.error('Failed to check Zoom auth:', error);
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Unknown error',
      );
    }
  }

  async createStoryFromRecording(
    params: CreateStoryFromRecordingParams,
  ): Promise<Result<{ videoUrl: string; platformPassword?: string }>> {
    const url = `${this.apiUrl}/api/zoom/create-story-from-recording`;

    try {
      const response = await authenticatedFetch(
        url,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            recordingId: params.recordingId,
            recordingFiles: params.recordingFiles,
            personId: params.personId,
            email: params.email,
            password: params.password,
          }),
        },
        this.getAuthToken,
      );

      if (!response.ok) {
        const errorData = await response.json();
        return err(
          'EXTERNAL',
          'Failed to prepare Zoom recording',
          errorData.code,
        );
      }

      const data = await response.json();
      return ok({
        videoUrl: data.data.videoUrl,
        platformPassword: data.data.platformPassword,
      });
    } catch (error) {
      logger.error('Failed to create story from recording:', error);
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Unknown error',
      );
    }
  }

  getProxyVideoUrl(params: {
    downloadUrl: string;
    personId?: string;
    email?: string;
    password?: string;
  }): string {
    const searchParams = new URLSearchParams();
    searchParams.set('downloadUrl', params.downloadUrl);
    if (params.personId) searchParams.set('personId', params.personId);
    if (params.email) searchParams.set('email', params.email);
    if (params.password) searchParams.set('password', params.password);
    return `${this.apiUrl}/api/zoom/proxy-video?${searchParams.toString()}`;
  }

  async disconnect(personId?: string, email?: string): Promise<Result<void>> {
    try {
      const response = await authenticatedFetch(
        `${this.apiUrl}/api/zoom/disconnect`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ personId, email }),
        },
        this.getAuthToken,
      );

      if (!response.ok) {
        const text = await response.text();
        return err('EXTERNAL', `Failed to disconnect Zoom: ${text}`);
      }

      return ok(undefined);
    } catch (error) {
      logger.error('Failed to disconnect Zoom:', error);
      return err(
        'EXTERNAL',
        error instanceof Error ? error.message : 'Unknown error',
      );
    }
  }
}
